import React from "react";
import { useTranslation } from "react-i18next";
import { StyledModal, StyledH2, StyledLabel } from "@aureskonnect/react-ui";
import { ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import { toast } from "react-toastify";
import { useSnapshot } from "valtio";
import { uuid } from "uuidv4";

import {
    setGlobalDataIsUpdated,
    setIsEdited,
    setIsLoading,
    setKey,
} from "../store";
import { store } from "@components/VerticalLayout/store";
import { store as LanguageStore } from "../store";

import { capitalize } from "@helpers/general";
import { CustomMainColorButton } from "@components/Common/CustomMainColorButton";
import { CustomSecondaryColorButton } from "@components/Common/CustomSecondaryColorButton";
import ErrorToast from "@components/Common/ErrorTost";
import { ShowSuccessToast } from "../ShowSuccessToast";
import { apiRequest, createLanguageItem } from "../hooks";
import { useFetchGlobal } from "../../../../api/hooks";

type ConfirmationModificationMessageType = {
    setModalState: Function;
    modalState: any;
    setLanguageFormState: Function;
    languageFormState: any;
};

export function ConfirmationModificationModal({
    setModalState,
    modalState,
    languageFormState,
    setLanguageFormState,
}: ConfirmationModificationMessageType) {
    const { fetchGlobal } = useFetchGlobal();
    const { t } = useTranslation();
    const { userID, franchiseID, operatorID } = useSnapshot(store);
    const { globalDataIsUpdated } = useSnapshot(LanguageStore);

    const uuidUser = localStorage.getItem("uuidUser");

    async function editLanguage(isValidation: any) {
        setIsLoading(true);
        toast.dismiss();
        let shopToUpdate = isValidation
            ? languageFormState.associated
            : [languageFormState.associated[0]];
        let objectData: any = {};
        let shopIds: any = [];
        try {
            shopToUpdate.forEach((el: any) => {
                if (!shopIds.includes(el.shopId.toString())) {
                    shopIds.push(el.shopId.toString());
                    objectData[el.shopId.toString()] = {};
                }
                const id: any = el.Id;
                Object.assign(objectData[el.shopId.toString()], {
                    ...createLanguageItem(
                        {
                            ...languageFormState.associated[0],
                            url_image: languageFormState.associated[0].urlImage,
                        },
                        id,
                        el.isActive,
                        el.isArchived,
                        el.isFavorite,
                        el.shopId
                    ),
                });
            });

            // prepare data for single API call
            const dataLanguage = {
                userId: userID,
                shopId: shopToUpdate.map((el: any) => el.shopId),
                franchiseId: franchiseID,
                operatorId: operatorID,
                uuidUser: uuidUser,
                data: objectData,
                isMultiShop: true,
            };
            const result: any = await apiRequest(
                dataLanguage,
                fetchGlobal,
                undefined,
                languageFormState.uploadedImage
            );
            if (result.error) {
                throw Error("error");
            }

            setIsLoading(false);
            ShowSuccessToast(`${t("The language was successfully edited")}`);
            setKey(uuid());
            setModalState({
                ...modalState,
                isAddLanguageClicked: false,
            });
            setGlobalDataIsUpdated(!globalDataIsUpdated);
        } catch (e) {
            setIsLoading(false);
            ErrorToast(`${t("There's an error")}`);
        }
    }

    return (
        <StyledModal
            toggle={true}
            isOpen={languageFormState.isOpened!}
            centered
        >
            <ModalHeader
                className="text-capitalize"
                toggle={() => {
                    editLanguage(false);
                    setLanguageFormState((prev: any) => ({
                        ...prev,
                        isOpened: !prev.isOpened,
                    }));
                    setModalState({
                        ...modalState,
                        isAddLanguageClicked: false,
                    });
                    setIsEdited(false);
                }}
            >
                <StyledH2>{capitalize(t("Alert"))}</StyledH2>
            </ModalHeader>
            <ModalBody>
                <StyledLabel
                    className="pl-3 m-0"
                    style={{ whiteSpace: "pre-line" }}
                >
                    {t(
                        'Do you want to apply these changes for all stores? \n Attention ! If you click on "Validate", the specific settings of the shops will be modified.'
                    )}
                </StyledLabel>
            </ModalBody>
            <ModalFooter className="border-top-0 ">
                <CustomSecondaryColorButton
                    outline
                    variant="light"
                    onClick={() => {
                        editLanguage(false);
                        setLanguageFormState((prev: any) => ({
                            ...prev,
                            isOpened: !prev.isOpened,
                        }));
                        setModalState({
                            ...modalState,
                            isAddLanguageClicked: false,
                        });
                        setIsEdited(false);
                    }}
                    rounded
                >
                    {t("Cancel")}
                </CustomSecondaryColorButton>
                <CustomMainColorButton
                    rounded
                    className="mr-2"
                    variant="primary"
                    onClick={() => {
                        editLanguage(true);
                        setLanguageFormState((prev: any) => ({
                            ...prev,
                            isOpened: !prev.isOpened,
                        }));
                        setModalState({
                            ...modalState,
                            isAddLanguageClicked: false,
                        });
                        setIsEdited(false);
                    }}
                >
                    {t("Validate")}
                </CustomMainColorButton>
            </ModalFooter>
        </StyledModal>
    );
}
